import React, { useState, useMemo, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, X, ChevronRight } from 'lucide-react';
import { categoryService } from '../../services/category-service';
import type { Category } from '../../types/category.types';
import { CategoryType } from '../../types/category.types';
import { getCategoryIconComponent } from '../../utils/icon-utils';
import { collectDescendantCategoryIds } from '../../utils/category-tree-utils';
import clsx from 'clsx';

export interface CategoryFilterDropdownProps {
  value: string[];
  onChange: (categoryIds: string[]) => void;
  className?: string;
  /**
   * filter: multi-select transaction filter (default)
   * select: single-category picker (e.g. budget limits)
   */
  mode?: 'filter' | 'select';
  /** Trigger label when nothing is selected */
  emptyLabel?: string;
  /** Limit listed categories by type */
  types?: CategoryType[];
  /** Hide categories with excludeFromSpending */
  hideExcludedFromSpending?: boolean;
  /** Category ids to omit from the list (e.g. already used on other rows) */
  excludeIds?: string[];
  /** aria-label for the trigger */
  'aria-label'?: string;
  disabled?: boolean;
}

interface CategoryGroup {
  parent: Category;
  children: Category[];
}

/** Stable default so omit/`[]` default does not re-create a new reference each render. */
const EMPTY_EXCLUDE_IDS: string[] = [];

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

export function CategoryFilterDropdown({
  value,
  onChange,
  className,
  mode = 'filter',
  emptyLabel,
  types,
  hideExcludedFromSpending = false,
  excludeIds = EMPTY_EXCLUDE_IDS,
  'aria-label': ariaLabel,
  disabled = false,
}: CategoryFilterDropdownProps): React.ReactElement {
  const isSelect = mode === 'select';
  const resolvedEmptyLabel = emptyLabel ?? (isSelect ? 'Select category' : 'All Categories');

  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const selectedSet = useMemo(() => new Set(value), [value]);
  const parentCheckboxRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch categories
  useEffect(() => {
    const loadCategories = async () => {
      setLoading(true);
      try {
        const result = await categoryService.listCategories({ includeSystem: true });
        if (result.data) {
          setCategories(result.data.categories);
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCategories();
  }, []);

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);

  const visibleCategories = useMemo(() => {
    return categories.filter((cat) => {
      if (excludeSet.has(cat.id)) return false;
      if (types && types.length > 0 && !types.includes(cat.type)) return false;
      if (hideExcludedFromSpending && cat.excludeFromSpending) return false;
      return true;
    });
  }, [categories, types, hideExcludedFromSpending, excludeSet]);

  // Build hierarchical structure
  const categoryGroups = useMemo(() => {
    const parentMap = new Map<string, CategoryGroup>();
    const allCategoriesMap = new Map<string, Category>();

    visibleCategories.forEach((cat) => {
      allCategoriesMap.set(cat.id, cat);
      if (!cat.parentId) {
        parentMap.set(cat.id, { parent: cat, children: [] });
      }
    });

    visibleCategories.forEach((cat) => {
      if (cat.parentId) {
        const parentGroup = parentMap.get(cat.parentId);
        if (parentGroup) {
          parentGroup.children.push(cat);
        } else if (!excludeSet.has(cat.id)) {
          // Orphaned subcategories (parent filtered out) still appear as top-level options
          parentMap.set(cat.id, { parent: cat, children: [] });
        }
      }
    });

    parentMap.forEach((group) => {
      group.children.sort((a, b) => a.name.localeCompare(b.name));
    });

    return Array.from(parentMap.values()).sort((a, b) =>
      a.parent.name.localeCompare(b.parent.name)
    );
  }, [visibleCategories, excludeSet]);

  const categoryGroupMap = useMemo(() => {
    const map = new Map<string, CategoryGroup>();
    categoryGroups.forEach((group) => {
      map.set(group.parent.id, group);
    });
    return map;
  }, [categoryGroups]);

  const descendantIdsFor = useCallback(
    (categoryId: string) => collectDescendantCategoryIds(categoryId, categories),
    [categories]
  );

  useEffect(() => {
    if (isSelect) return;
    categoryGroups.forEach((group) => {
      const checkbox = parentCheckboxRefs.current[group.parent.id];
      if (!checkbox) {
        return;
      }
      const familyIds = descendantIdsFor(group.parent.id);
      const selectedFamilyCount = [...familyIds].filter((id) => selectedSet.has(id)).length;
      const allFamilySelected = selectedFamilyCount === familyIds.size;
      checkbox.indeterminate = selectedFamilyCount > 0 && !allFamilySelected;
    });
  }, [categoryGroups, selectedSet, isSelect, descendantIdsFor]);

  const childToParentMap = useMemo(() => {
    const map = new Map<string, string>();
    categoryGroups.forEach((group) => {
      group.children.forEach((child) => {
        map.set(child.id, group.parent.id);
      });
    });
    return map;
  }, [categoryGroups]);

  // Filter categories based on search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) {
      return categoryGroups;
    }

    const query = searchQuery.toLowerCase();
    const filtered: CategoryGroup[] = [];

    categoryGroups.forEach((group) => {
      const parentMatches = group.parent.name.toLowerCase().includes(query);
      const matchingChildren = group.children.filter((child) =>
        child.name.toLowerCase().includes(query)
      );

      if (parentMatches || matchingChildren.length > 0) {
        filtered.push({
          parent: group.parent,
          children: parentMatches ? group.children : matchingChildren,
        });
      }
    });

    return filtered;
  }, [categoryGroups, searchQuery]);

  // Auto-expand groups that have selected children or match search
  useEffect(() => {
    const next = new Set<string>();
    if (searchQuery.trim()) {
      filteredGroups.forEach((group) => {
        if (group.children.length > 0) {
          next.add(group.parent.id);
        }
      });
    } else {
      filteredGroups.forEach((group) => {
        const hasSelectedChild = group.children.some((child) => selectedSet.has(child.id));
        if (hasSelectedChild) {
          next.add(group.parent.id);
        }
      });
    }
    // Avoid re-render when membership is unchanged (new Set() each time still trips React)
    setExpandedGroups((prev) => (setsEqual(prev, next) ? prev : next));
  }, [filteredGroups, selectedSet, searchQuery]);

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const menuHeight = 400;
    const openUp = spaceBelow < menuHeight && rect.top > spaceBelow;
    const maxHeight = Math.min(384, openUp ? rect.top - 12 : spaceBelow - 12);

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 320),
      maxHeight: Math.max(maxHeight, 200),
      zIndex: 100,
      ...(openUp
        ? { bottom: viewportH - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  const closeMenu = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const toggleGroupExpansion = (parentId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  // Get selected category label (summary)
  const selectedCategoryLabel = useMemo(() => {
    if (!value || value.length === 0) {
      return resolvedEmptyLabel;
    }

    if (isSelect && value.length === 1) {
      return categories.find((cat) => cat.id === value[0])?.name ?? resolvedEmptyLabel;
    }

    const valueSet = new Set(value);
    const namesToDisplay = new Set<string>();

    value.forEach((id) => {
      const category = categories.find((cat) => cat.id === id);
      if (!category) {
        return;
      }
      if (category.parentId && valueSet.has(category.parentId)) {
        return;
      }
      namesToDisplay.add(category.name);
    });

    const names = Array.from(namesToDisplay);

    if (names.length === 1) {
      return names[0];
    }
    if (names.length === 2) {
      return names.join(', ');
    }
    return `${names.length} categories selected`;
  }, [value, categories, resolvedEmptyLabel, isSelect]);

  const handleClearSelection = () => {
    if (value.length === 0) {
      if (isSelect) closeMenu();
      return;
    }
    onChange([]);
    if (isSelect) closeMenu();
  };

  const handleSelectSingle = (categoryId: string) => {
    onChange([categoryId]);
    closeMenu();
  };

  const handleToggleParent = (parentId: string) => {
    if (isSelect) {
      handleSelectSingle(parentId);
      return;
    }

    const group = categoryGroupMap.get(parentId);
    if (!group) {
      return;
    }
    const allIds = [...descendantIdsFor(parentId)];
    const next = new Set(selectedSet);
    const isFullySelected = allIds.every((id) => next.has(id));

    if (isFullySelected) {
      allIds.forEach((id) => next.delete(id));
    } else {
      allIds.forEach((id) => next.add(id));
    }

    onChange(Array.from(next));
  };

  const handleToggleChild = (childId: string, parentId?: string | null) => {
    if (isSelect) {
      handleSelectSingle(childId);
      return;
    }

    const next = new Set(selectedSet);

    if (next.has(childId)) {
      next.delete(childId);
    } else {
      next.add(childId);
    }

    if (parentId) {
      const familyIds = descendantIdsFor(parentId);
      const allFamilySelected = [...familyIds].every((id) => next.has(id));
      if (allFamilySelected) {
        next.add(parentId);
      } else {
        next.delete(parentId);
      }
    }

    onChange(Array.from(next));
  };

  const menu =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <>
            <div className="fixed inset-0 z-[99]" onClick={closeMenu} aria-hidden="true" />
            <div
              ref={menuRef}
              style={menuStyle}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden flex flex-col"
            >
              {/* Search Box */}
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search categories..."
                    className="w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Category List */}
              <div className="overflow-y-auto flex-1 min-h-0">
                {loading ? (
                  <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    Loading categories...
                  </div>
                ) : filteredGroups.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No categories found
                  </div>
                ) : (
                  <>
                    {!isSelect && (
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        className={clsx(
                          'w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between',
                          value.length === 0
                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        )}
                        aria-pressed={value.length === 0}
                      >
                        <span>All Categories</span>
                        {value.length === 0 && (
                          <span className="text-xs font-semibold text-primary-700 dark:text-primary-300">
                            Selected
                          </span>
                        )}
                      </button>
                    )}

                    {isSelect && value.length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700"
                      >
                        Clear selection
                      </button>
                    )}

                    {filteredGroups.map((group) => {
                      const ParentIcon = getCategoryIconComponent(group.parent);
                      const parentSelected = selectedSet.has(group.parent.id);
                      const isExpanded = expandedGroups.has(group.parent.id);
                      const hasChildren = group.children.length > 0;
                      return (
                        <div
                          key={group.parent.id}
                          className="border-t border-gray-100 dark:border-gray-700"
                        >
                          <div className="flex items-center">
                            {hasChildren && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleGroupExpansion(group.parent.id);
                                }}
                                className="px-2 py-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                aria-label={
                                  isExpanded
                                    ? `Collapse ${group.parent.name}`
                                    : `Expand ${group.parent.name}`
                                }
                                aria-expanded={isExpanded}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                                )}
                              </button>
                            )}
                            {!hasChildren && <div className="w-10" />}
                            <label
                              className={clsx(
                                'flex-1 px-2 py-2.5 text-sm transition-colors flex items-center gap-3 cursor-pointer',
                                parentSelected
                                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              )}
                            >
                              <input
                                ref={(node) => {
                                  parentCheckboxRefs.current[group.parent.id] = node;
                                }}
                                type={isSelect ? 'radio' : 'checkbox'}
                                name={isSelect ? 'category-select' : undefined}
                                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                checked={parentSelected}
                                onChange={() => handleToggleParent(group.parent.id)}
                                aria-label={
                                  isSelect
                                    ? `Select ${group.parent.name}`
                                    : `Select ${group.parent.name} and its subcategories`
                                }
                              />
                              {ParentIcon && (
                                <ParentIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                              )}
                              <span className="font-medium">{group.parent.name}</span>
                              {hasChildren && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                                  ({group.children.length})
                                </span>
                              )}
                            </label>
                          </div>

                          {hasChildren && isExpanded && (
                            <div className="bg-gray-50 dark:bg-gray-900/50">
                              {group.children.map((child) => {
                                const ChildIcon = getCategoryIconComponent(child);
                                const childSelected = selectedSet.has(child.id);
                                const parentId =
                                  child.parentId ||
                                  childToParentMap.get(child.id) ||
                                  group.parent.id;
                                return (
                                  <label
                                    key={child.id}
                                    className={clsx(
                                      'w-full px-12 py-2 text-sm transition-colors flex items-center gap-3 cursor-pointer',
                                      childSelected
                                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    )}
                                  >
                                    <input
                                      type={isSelect ? 'radio' : 'checkbox'}
                                      name={isSelect ? 'category-select' : undefined}
                                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                      checked={childSelected}
                                      onChange={() => handleToggleChild(child.id, parentId)}
                                      aria-label={`Select ${child.name}`}
                                    />
                                    {ChildIcon && (
                                      <ChildIcon
                                        className="h-3.5 w-3.5 flex-shrink-0"
                                        aria-hidden="true"
                                      />
                                    )}
                                    <span>{child.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <div className={clsx('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="flex items-center justify-between w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 min-w-[180px] disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label={ariaLabel ?? (isSelect ? 'Select category' : 'Filter by category')}
        aria-expanded={isOpen}
      >
        <span className="text-sm truncate">{selectedCategoryLabel}</span>
        <ChevronDown
          className={clsx(
            'h-4 w-4 ml-2 flex-shrink-0 transition-transform',
            isOpen && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </button>
      {menu}
    </div>
  );
}
